/**
 * Solar dimensioning calculator – Configurable Edition
 *
 * All constants are now configurable via SolarConfig.
 * Fallback to DEFAULT_SOLAR_CONFIG ensures backward compatibility.
 */

export type ConnectionType = "monofasico" | "bifasico" | "trifasico";

// ── Configurable solar calculation parameters ──────────────────

export interface SolarConfig {
  performanceRatio: number;
  panelWatts: number;
  hsp: number;
  defaultTariff: number;
  energyInflationRate: number;
  co2Factor: number;
  co2PerTree: number;
  minKwhMonofasico: number;
  minKwhBifasico: number;
  minKwhTrifasico: number;
  panelDegradationPct: number;
  sizingMarginPct: number;
  // Individual losses (used to compute combined PR)
  cableLossPct?: number;
  inverterEfficiency?: number;
  temperatureLossPct?: number;
  dirtLossPct?: number;
}

export const DEFAULT_SOLAR_CONFIG: SolarConfig = {
  performanceRatio: 0.80,
  panelWatts: 550,
  hsp: 4.5,
  defaultTariff: 0.90,
  energyInflationRate: 0.06,
  co2Factor: 0.075,
  co2PerTree: 22,
  minKwhMonofasico: 30,
  minKwhBifasico: 50,
  minKwhTrifasico: 100,
  panelDegradationPct: 0.005,
  sizingMarginPct: 0.10,
  cableLossPct: 0.02,
  inverterEfficiency: 0.97,
  temperatureLossPct: 0.03,
  dirtLossPct: 0.02,
};

// ── Helper: connection minimums from config ────────────────────

function getConnectionMinKwh(config: SolarConfig, connectionType: ConnectionType): number {
  const map: Record<ConnectionType, number> = {
    monofasico: config.minKwhMonofasico,
    bifasico: config.minKwhBifasico,
    trifasico: config.minKwhTrifasico,
  };
  return map[connectionType];
}

export const CONNECTION_LABELS: Record<ConnectionType, string> = {
  monofasico: "Monofásico (30 kWh)",
  bifasico: "Bifásico (50 kWh)",
  trifasico: "Trifásico (100 kWh)",
};

// Keep legacy export for backward compat
export const CONNECTION_MIN_KWH: Record<ConnectionType, number> = {
  monofasico: 30,
  bifasico: 50,
  trifasico: 100,
};

// ── Combined PR calculation ────────────────────────────────────

export interface SystemLosses {
  cableLossPct: number;
  inverterEfficiency: number;
  temperatureLossPct: number;
  dirtLossPct: number;
}

/**
 * Calculate combined Performance Ratio from individual losses.
 * PR = (1 - cable) × (1 - temp) × (1 - dirt) × inverter_efficiency
 */
export function calculateCombinedPR(losses: SystemLosses): number {
  return (
    (1 - losses.cableLossPct) *
    (1 - losses.temperatureLossPct) *
    (1 - losses.dirtLossPct) *
    losses.inverterEfficiency
  );
}

// ── Effective HSP for a roof face ──────────────────────────────

export interface RoofFaceFactors {
  orientationFactor: number;
  tiltFactor: number;
  shadingPct: number;
  roofLossFactor: number;
}

/**
 * Calculate effective HSP for a specific roof face.
 * hsp_efetivo = hsp_base × orientation × tilt × (1 - shading) × (1 - roofLoss)
 */
export function calculateEffectiveHSP(baseHsp: number, factors: RoofFaceFactors): number {
  return (
    baseHsp *
    factors.orientationFactor *
    factors.tiltFactor *
    (1 - factors.shadingPct) *
    (1 - factors.roofLossFactor)
  );
}

// ── Multi-roof dimensioning ────────────────────────────────────

export interface RoofFaceInput {
  profileName: string;
  profileId?: string;
  factors: RoofFaceFactors;
  areaM2?: number;
  consumptionSharePct: number; // percentage of total consumption for this face (0-100)
}

export interface RoofFaceResult {
  profileName: string;
  profileId?: string;
  effectiveHsp: number;
  powerKwp: number;
  panelCount: number;
  estimatedGenerationKwh: number;
}

export interface MultiRoofResult {
  faces: RoofFaceResult[];
  totalPowerKwp: number;
  totalPanelCount: number;
  totalGenerationKwh: number;
}

/**
 * Calculate dimensioning distributed across multiple roof faces.
 */
export function calculateMultiRoofDimensioning(
  totalConsumptionKwh: number,
  faces: RoofFaceInput[],
  config: SolarConfig = DEFAULT_SOLAR_CONFIG,
): MultiRoofResult {
  const pr = config.performanceRatio;
  const panelWatts = config.panelWatts;
  const margin = 1 + config.sizingMarginPct;

  const results: RoofFaceResult[] = faces.map((face) => {
    const faceConsumption = totalConsumptionKwh * (face.consumptionSharePct / 100);
    const effectiveHsp = calculateEffectiveHSP(config.hsp, face.factors);
    const dailyGeneration = faceConsumption / 30;
    const powerKwp = Math.ceil(((dailyGeneration * margin) / (effectiveHsp * pr)) * 100) / 100;
    const panelCount = Math.ceil((powerKwp * 1000) / panelWatts);
    const estimatedGenerationKwh = Math.round(
      (panelCount * panelWatts * effectiveHsp * pr * 30) / 1000,
    );

    return {
      profileName: face.profileName,
      profileId: face.profileId,
      effectiveHsp: Math.round(effectiveHsp * 100) / 100,
      powerKwp,
      panelCount,
      estimatedGenerationKwh,
    };
  });

  return {
    faces: results,
    totalPowerKwp: Math.round(results.reduce((s, f) => s + f.powerKwp, 0) * 100) / 100,
    totalPanelCount: results.reduce((s, f) => s + f.panelCount, 0),
    totalGenerationKwh: results.reduce((s, f) => s + f.estimatedGenerationKwh, 0),
  };
}

// ── Main dimensioning result ───────────────────────────────────

export interface SolarDimensioning {
  consumptionKwh: number;
  billValue: number;
  powerKwp: number;
  panelCount: number;
  estimatedGenerationKwh: number;
  estimatedSavingsYear: number;
  tariffUsed: number;
  connectionType: ConnectionType;
  connectionMinKwh: number;
  cipValue: number;
  monthlySavings: number;
  paybackYears: number;
  economy25Years: number;
  co2AvoidedKgYear: number;
  treesEquivalent: number;
}

// ── Main calculation function ──────────────────────────────────

export function calculateSolarDimensioning(
  consumptionKwh: number,
  billValue: number,
  tariffKwh: number = 0,
  connectionType: ConnectionType = "bifasico",
  cipValue: number = 0,
  projectTotalValue: number = 0,
  config: SolarConfig = DEFAULT_SOLAR_CONFIG,
): SolarDimensioning {
  const pr = config.performanceRatio;
  const hsp = config.hsp;
  const panelWatts = config.panelWatts;
  const margin = 1 + config.sizingMarginPct;

  // Determine tariff
  const effectiveTariff =
    tariffKwh > 0
      ? tariffKwh
      : consumptionKwh > 0 && billValue > 0
        ? billValue / consumptionKwh
        : config.defaultTariff;

  // If only bill value provided, estimate consumption
  const effectiveConsumption = consumptionKwh > 0 ? consumptionKwh : billValue / effectiveTariff;

  // Required daily generation
  const dailyGeneration = effectiveConsumption / 30;

  // Required power (kWp) with sizing margin
  const powerKwp = Math.ceil(((dailyGeneration * margin) / (hsp * pr)) * 100) / 100;

  // Number of panels
  const panelCount = Math.ceil((powerKwp * 1000) / panelWatts);

  // Actual estimated monthly generation
  const estimatedGenerationKwh = Math.round(
    (panelCount * panelWatts * hsp * pr * 30) / 1000,
  );

  // Connection minimum
  const connectionMinKwh = getConnectionMinKwh(config, connectionType);

  // Minimum bill the client will always pay with solar
  const contaMinimaComSolar = connectionMinKwh * effectiveTariff + cipValue;

  // Monthly savings = current bill - minimum bill with solar
  const monthlySavings = Math.max(0, Math.round((billValue - contaMinimaComSolar) * 100) / 100);

  const estimatedSavingsYear = Math.round(monthlySavings * 12 * 100) / 100;

  // Payback
  const paybackYears =
    monthlySavings > 0 && projectTotalValue > 0
      ? Math.round((projectTotalValue / (monthlySavings * 12)) * 10) / 10
      : 0;

  // Economy in 25 years with energy inflation
  let economy25Years = 0;
  for (let year = 1; year <= 25; year++) {
    // Apply panel degradation: generation decreases each year
    const degradationFactor = Math.pow(1 - config.panelDegradationPct, year - 1);
    economy25Years +=
      monthlySavings * 12 * degradationFactor * Math.pow(1 + config.energyInflationRate, year - 1);
  }
  economy25Years = Math.round(economy25Years * 100) / 100;

  // Sustainability metrics
  const co2AvoidedKgYear = Math.round(estimatedGenerationKwh * 12 * config.co2Factor);
  const treesEquivalent = Math.round(co2AvoidedKgYear / config.co2PerTree);

  return {
    consumptionKwh: Math.round(effectiveConsumption),
    billValue,
    powerKwp,
    panelCount,
    estimatedGenerationKwh,
    estimatedSavingsYear,
    tariffUsed: Math.round(effectiveTariff * 100) / 100,
    connectionType,
    connectionMinKwh,
    cipValue,
    monthlySavings: Math.round(monthlySavings * 100) / 100,
    paybackYears,
    economy25Years,
    co2AvoidedKgYear,
    treesEquivalent,
  };
}

/** Calculate economy comparison for N years with inflation */
export function calculateEconomyComparison(
  monthlySavings: number,
  monthlyBill: number,
  connectionMinKwh: number,
  tariff: number,
  cipValue: number,
  years: number,
  config: SolarConfig = DEFAULT_SOLAR_CONFIG,
): { withoutSolar: number; withSolar: number; savings: number } {
  let withoutSolar = 0;
  let withSolar = 0;
  for (let y = 1; y <= years; y++) {
    const inflationFactor = Math.pow(1 + config.energyInflationRate, y - 1);
    withoutSolar += monthlyBill * 12 * inflationFactor;
    withSolar += (connectionMinKwh * tariff + cipValue) * 12 * inflationFactor;
  }
  return {
    withoutSolar: Math.round(withoutSolar),
    withSolar: Math.round(withSolar),
    savings: Math.round(withoutSolar - withSolar),
  };
}

// ── Simplified dimensioning (irradiation-factor mode) ─────────

/**
 * Simplified solar dimensioning using a state-level irradiation factor.
 * Formula: panels = ceil(consumption / (panelPower_kW) / irradiationFactor * (1 + margin))
 * Generation: kWp × irradiationFactor
 */
export function calculateSimplifiedDimensioning(
  consumptionKwh: number,
  panelPowerW: number,
  irradiationFactor: number,
  sizingMarginPct: number,
  billValue: number,
  tariffKwh: number,
  projectTotalValue: number,
  config: SolarConfig = DEFAULT_SOLAR_CONFIG,
  connectionType: ConnectionType = "bifasico",
  cipValue: number = 0,
): SolarDimensioning {
  const panelKw = panelPowerW / 1000;

  // Determine tariff
  const effectiveTariff =
    tariffKwh > 0
      ? tariffKwh
      : consumptionKwh > 0 && billValue > 0
        ? billValue / consumptionKwh
        : config.defaultTariff;

  // If only bill value provided, estimate consumption
  const effectiveConsumption = consumptionKwh > 0 ? consumptionKwh : billValue / effectiveTariff;

  // Panel count with margin
  const rawPanelCount = effectiveConsumption / panelKw / irradiationFactor;
  const panelCount = Math.ceil(rawPanelCount * (1 + sizingMarginPct));

  // Power
  const powerKwp = parseFloat((panelCount * panelPowerW / 1000).toFixed(2));

  // Estimated generation
  const estimatedGenerationKwh = Math.round(powerKwp * irradiationFactor);

  // Connection minimum from config
  const connectionMinKwh = getConnectionMinKwh(config, connectionType);

  // Monthly savings
  const contaMinimaComSolar = connectionMinKwh * effectiveTariff + cipValue;
  const monthlySavings = Math.max(0, Math.round((billValue - contaMinimaComSolar) * 100) / 100);
  const estimatedSavingsYear = Math.round(monthlySavings * 12 * 100) / 100;

  // Payback
  const paybackYears =
    monthlySavings > 0 && projectTotalValue > 0
      ? Math.round((projectTotalValue / (monthlySavings * 12)) * 10) / 10
      : 0;

  // Economy 25 years
  let economy25Years = 0;
  for (let year = 1; year <= 25; year++) {
    const degradationFactor = Math.pow(1 - config.panelDegradationPct, year - 1);
    economy25Years +=
      monthlySavings * 12 * degradationFactor * Math.pow(1 + config.energyInflationRate, year - 1);
  }
  economy25Years = Math.round(economy25Years * 100) / 100;

  // Sustainability
  const co2AvoidedKgYear = Math.round(estimatedGenerationKwh * 12 * config.co2Factor);
  const treesEquivalent = Math.round(co2AvoidedKgYear / config.co2PerTree);

  return {
    consumptionKwh: Math.round(effectiveConsumption),
    billValue,
    powerKwp,
    panelCount,
    estimatedGenerationKwh,
    estimatedSavingsYear,
    tariffUsed: Math.round(effectiveTariff * 100) / 100,
    connectionType,
    connectionMinKwh,
    cipValue,
    monthlySavings: Math.round(monthlySavings * 100) / 100,
    paybackYears,
    economy25Years,
    co2AvoidedKgYear,
    treesEquivalent,
  };
}
